/**
 * Schools (SRO) and the paper forms BlueHQ fills in and prints: lockdown drill worksheet, NCIC entry/deletion
 * request, custodial promise, rights form, and the extra boxes on the tow report and the Uniform Traffic Ticket.
 */

// ─── Lockdown drills ───────────────────────────────────────────────────────
export const SCHOOL_LEVELS = ['elementary', 'middle', 'high', 'k12', 'other'] as const;
export type SchoolLevel = (typeof SCHOOL_LEVELS)[number];
export const SCHOOL_LEVEL_LABEL: Record<SchoolLevel, string> = { elementary: 'Elementary', middle: 'Middle', high: 'High', k12: 'K–12', other: 'Other' };

/** The five checks on every room, in the worksheet's order. */
export const LOCKDOWN_CHECKS = [
  { key: 'locks', label: 'Locks', ask: 'Door locked?' },
  { key: 'lights', label: 'Lights', ask: 'Lights off?' },
  { key: 'outOfSight', label: 'Out of sight', ask: 'Everyone out of sight of the door?' },
  { key: 'doorKnock', label: 'Door knock', ask: 'Ignored the knock (no one opened or answered)?' },
  { key: 'optionsUsed', label: 'Options used', ask: 'Used their options (barricade, cover, exits)?' },
] as const;
export type LockdownCheck = (typeof LOCKDOWN_CHECKS)[number]['key'];
export type LockdownRoom = { room: string; note?: string | null } & { [K in LockdownCheck]: boolean | null };

export function blankRoom(room: string): LockdownRoom {
  return { room, locks: null, lights: null, outOfSight: null, doorKnock: null, optionsUsed: null, note: null };
}

/** How a drill went: rooms checked, rooms with any "No", and every miss. */
export function drillScore(rooms: LockdownRoom[]) {
  const checked = rooms.filter((r) => LOCKDOWN_CHECKS.some((c) => r[c.key] !== null));
  const misses: { room: string; check: string }[] = [];
  for (const r of rooms) for (const c of LOCKDOWN_CHECKS) if (r[c.key] === false) misses.push({ room: r.room, check: c.label });
  const failed = new Set(misses.map((m) => m.room));
  const answered = rooms.reduce((n, r) => n + LOCKDOWN_CHECKS.filter((c) => r[c.key] !== null).length, 0);
  const passed = rooms.reduce((n, r) => n + LOCKDOWN_CHECKS.filter((c) => r[c.key] === true).length, 0);
  return { rooms: rooms.length, checked: checked.length, roomsWithMisses: failed.size, misses, pct: answered ? Math.round((passed / answered) * 100) : null };
}

export function stopwatchText(sec: number | null | undefined) {
  if (sec == null) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─── Case forms ────────────────────────────────────────────────────────────
export const FORM_KINDS = ['ncic', 'custodial', 'rights'] as const;
export type FormKind = (typeof FORM_KINDS)[number];
export const FORM_LABEL: Record<FormKind, string> = { ncic: 'NCIC entry/deletion request', custodial: 'Custodial promise', rights: 'Rights form' };
export const FORM_PREFIX: Record<FormKind, string> = { ncic: 'NC', custodial: 'CP', rights: 'RF' };

/** The rights form, line by line (each initialed). */
export const RIGHTS_LINES = [
  'Before we ask you any questions, you must understand your rights.',
  'You have the right to remain silent.',
  'Anything you say can be used against you in court.',
  'You have the right to talk to a lawyer for advice before any questions and to have him/her with you during questioning.',
  'If you cannot afford a lawyer, one will be appointed for you before any questioning, if you wish.',
  'If you decide to answer questions now without a lawyer present, you will still have the right to stop answering at any time until you talk to a lawyer.',
] as const;
export const RIGHTS_WAIVER =
  'I have read this statement of my rights and I understand what my rights are. I am willing to make a statement and answer questions. I do not want a lawyer at this time. No promises or threats have been made to me and no pressure or coercion (force) of any kind has been used against me.';
/** What happened after the rights were read. */
export const RIGHTS_OUTCOMES = [
  { code: 'waived', label: 'Waived — signed the waiver and will talk' },
  { code: 'refused_sign', label: 'Will talk but refused to sign' },
  { code: 'invoked_silence', label: 'Chose to remain silent' },
  { code: 'invoked_lawyer', label: 'Asked for a lawyer' },
] as const;
export type RightsOutcome = (typeof RIGHTS_OUTCOMES)[number]['code'];

export const CUSTODIAL_PROMISE_TEXT =
  'have been advised that the following charges have been brought against the aforesaid child and do hereby promise to bring the child at a time and place designated by the court in the event of further action.';

export const NCIC_PERSON_KINDS = [
  { code: 'wanted', label: 'Wanted' },
  { code: 'missing', label: 'Missing' },
  { code: 'runaway', label: 'Runaway' },
  { code: 'bolo', label: 'BOLO' },
] as const;
export const NCIC_PROPERTY_STATUS = [
  { code: 'stolen', label: 'Stolen' },
  { code: 'recovered', label: 'Recovered' },
  { code: 'found', label: 'Found' },
  { code: 'towed', label: 'Towed' },
  { code: 'suspect', label: 'Suspect' },
  { code: 'victim', label: 'Victim' },
] as const;
export const NCIC_PROPERTY_TYPES = [
  { code: 'vehicle', label: 'Vehicle' },
  { code: 'gun', label: 'Gun' },
  { code: 'boat', label: 'Boat' },
  { code: 'plate', label: 'License plate' },
  { code: 'securities', label: 'Securities / bonds / stocks' },
  { code: 'article', label: 'Article' },
] as const;

/** ***-**-1234 */
export function maskSsn(ssn: string | null | undefined) {
  if (!ssn) return null;
  const d = ssn.replace(/\D/g, '');
  return d.length >= 4 ? `***-**-${d.slice(-4)}` : '***';
}
export function cleanSsn(s: string | null | undefined): string | null {
  if (!s) return null;
  const d = s.replace(/\D/g, '');
  if (!d) return null;
  if (d.length !== 9) throw new Error('A Social Security number has 9 digits');
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

// ─── Tow report ────────────────────────────────────────────────────────────
/** The damage diagram's spots (numbered like the paper form). */
export const TOW_DAMAGE_SPOTS = [
  { n: 1, label: 'Front' },
  { n: 2, label: 'Right front' },
  { n: 3, label: 'Right side' },
  { n: 4, label: 'Right rear' },
  { n: 5, label: 'Rear' },
  { n: 6, label: 'Left rear' },
  { n: 7, label: 'Left side' },
  { n: 8, label: 'Left front' },
  { n: 9, label: 'Hood' },
  { n: 10, label: 'Roof' },
  { n: 11, label: 'Trunk' },
] as const;

// ─── Uniform Traffic Ticket ────────────────────────────────────────────────
export const TICKET_VEHICLE_TYPES = [
  { code: 'auto', label: 'Auto' },
  { code: 'motorcycle', label: 'Motorcycle' },
  { code: 'moped', label: 'Moped' },
  { code: 'bicycle', label: 'Bicycle' },
  { code: 'pedestrian', label: 'Pedestrian' },
  { code: 'commercial', label: 'Commercial vehicle' },
  { code: 'combination', label: 'Combination' },
  { code: 'passenger16', label: '16+ passenger vehicle' },
  { code: 'hazmat', label: 'Hazardous materials' },
  { code: 'other', label: 'Other' },
] as const;
export const TICKET_HAIR = ['BLK', 'BRO', 'BLN', 'RED', 'GRY', 'WHI', 'SDY', 'BAL', 'XXX'] as const;
export const TICKET_EYES = ['BRO', 'BLU', 'GRN', 'HAZ', 'GRY', 'BLK', 'MAR', 'XXX'] as const;
